import { useEffect } from "react";

/**
 * AM-5828 — user directive: these forms must never blank out without an
 * error message in the console or anything at all. This is the last-resort
 * backstop for that: window-level `error` and `unhandledrejection` listeners
 * that `console.error` a clearly-tagged message, so a future failure in this
 * area can never be silently invisible even if nothing else logs it.
 *
 * Intentionally small and dependency-free — a backstop, not a telemetry
 * pipeline. Neither listener calls `preventDefault()`, so the browser's own
 * default handling (and any existing telemetry/App-Insights hook) is
 * unaffected.
 *
 * Mount once for the whole app.
 */
export function useGlobalErrorReporter(): void {
  useEffect(() => {
    const onError = (event: ErrorEvent) => {
      console.error("[global-error]", event.error ?? event.message, event);
    };
    const onUnhandledRejection = (event: PromiseRejectionEvent) => {
      console.error("[unhandled-rejection]", event.reason, event);
    };

    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onUnhandledRejection);

    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onUnhandledRejection);
    };
  }, []);
}
